(() => {
  'use strict';

  const SELECTOR = '[data-tobfun-free-gifts]';
  const modules = new Set();
  const originalFetch = window.fetch.bind(window);
  let fetchPatched = false;
  let lastSubmittedForm = null;

  const normalize = (value) => String(value || '').trim().toLowerCase();
  const asPositiveInteger = (value, fallback = 1) => {
    const number = Number.parseInt(value, 10);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  };

  function parseVariants(element) {
    try {
      return JSON.parse(element.querySelector('.tobfun-free-gifts__variants')?.textContent || '[]');
    } catch (error) {
      return [];
    }
  }

  function buildOffer(element) {
    const triggerOne = normalize(element.dataset.triggerOne);
    const triggerTwo = normalize(element.dataset.triggerTwo);
    const eligibleVariantIds = new Set(
      parseVariants(element)
        .filter((variant) => {
          const options = (variant.options || []).map(normalize);
          return options.includes(triggerOne) && options.includes(triggerTwo);
        })
        .map((variant) => String(variant.id))
    );
    const giftVariantIds = (element.dataset.giftVariantIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    return {
      element,
      context: element.dataset.context,
      eligibleVariantIds,
      giftVariantIds,
      active: false
    };
  }

  function selectedProductVariantId(offer) {
    const scope = offer.element.closest('[id^="MainProduct-"]') || offer.element.parentElement;
    const input = scope.querySelector('product-form input.product-variant-id[name="id"], form[data-type="add-to-cart-form"] input.product-variant-id[name="id"]');
    return input && !input.disabled ? String(input.value) : '';
  }

  function selectedBundleVariantIds(offer) {
    const bundle = offer.element.closest('bundle-deals');
    if (!bundle) return [];
    return Array.from(bundle.querySelectorAll('.bundle-deals__checkbox-js:checked'))
      .map((checkbox) => String(checkbox.dataset.id || ''))
      .filter(Boolean);
  }

  function refreshOffer(offer) {
    let active = false;
    if (offer.context === 'bundle') {
      active = selectedBundleVariantIds(offer).some((id) => offer.eligibleVariantIds.has(id));
    } else {
      active = offer.eligibleVariantIds.has(selectedProductVariantId(offer));
    }

    offer.active = active;
    offer.element.hidden = !active;
    offer.element.setAttribute('aria-hidden', active ? 'false' : 'true');

    if (offer.context === 'product') {
      const productSection = offer.element.closest('[id^="MainProduct-"]') || offer.element.parentElement;
      productSection?.classList.toggle('tobfun-free-gifts-active', active);
    }
  }

  function refreshAll() {
    modules.forEach((offer) => {
      if (!offer.element.isConnected) {
        modules.delete(offer);
        return;
      }
      refreshOffer(offer);
    });
  }

  function initialize(root = document) {
    root.querySelectorAll(SELECTOR).forEach((element) => {
      if (element.dataset.tobfunFreeGiftsInitialized === 'true') return;
      element.dataset.tobfunFreeGiftsInitialized = 'true';
      modules.add(buildOffer(element));
    });
    refreshAll();
  }

  function addPair(map, index, field, value) {
    const key = String(index || '0');
    if (!map.has(key)) map.set(key, {});
    map.get(key)[field] = value;
  }

  function entriesToRequest(entries) {
    const indexed = new Map();
    const plain = {};
    let sections = null;
    let sectionsUrl = null;
    let blankIndex = -1;

    for (const [key, rawValue] of entries) {
      const value = String(rawValue);
      const match = key.match(/^items\[([^\]]*)\]\[(id|quantity)\]$/);
      if (match) {
        let index = match[1];
        if (!index && match[2] === 'id') blankIndex += 1;
        if (!index) index = `blank-${Math.max(blankIndex, 0)}`;
        addPair(indexed, index, match[2], value);
      } else if (key === 'id' || key === 'quantity') {
        plain[key] = value;
      } else if (key === 'sections') {
        sections = value;
      } else if (key === 'sections_url') {
        sectionsUrl = value;
      }
    }

    const items = Array.from(indexed.values())
      .filter((item) => item.id)
      .map((item) => ({ id: String(item.id), quantity: asPositiveInteger(item.quantity) }));
    if (!items.length && plain.id) {
      items.push({ id: String(plain.id), quantity: asPositiveInteger(plain.quantity) });
    }
    return { items, sections, sectionsUrl };
  }

  function objectToRequest(body) {
    const items = Array.isArray(body?.items)
      ? body.items.map((item) => ({ id: String(item.id), quantity: asPositiveInteger(item.quantity) }))
      : body?.id
        ? [{ id: String(body.id), quantity: asPositiveInteger(body.quantity) }]
        : [];
    return {
      items,
      sections: body?.sections || null,
      sectionsUrl: body?.sections_url || null
    };
  }

  async function parseRequest(input, init = {}) {
    const requestUrl = typeof input === 'string' ? input : input?.url || '';
    if (!/(^|\/)cart\/add(?:\.js)?(?:\?|$)/.test(new URL(requestUrl, window.location.origin).pathname + new URL(requestUrl, window.location.origin).search)) {
      return null;
    }

    let body = init.body;
    if (!body && typeof Request !== 'undefined' && input instanceof Request) {
      const clone = input.clone();
      const contentType = clone.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        body = await clone.json();
      } else {
        try {
          body = await clone.formData();
        } catch (error) {
          body = null;
        }
      }
    }

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return entriesToRequest(body.entries());
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return entriesToRequest(body.entries());
    }
    if (typeof body === 'string') {
      try {
        return objectToRequest(JSON.parse(body));
      } catch (error) {
        return entriesToRequest(new URLSearchParams(body).entries());
      }
    }
    if (body && typeof body === 'object') return objectToRequest(body);
    return null;
  }

  function activeOfferFor(items) {
    refreshAll();
    const requestedIds = new Set(items.map((item) => String(item.id)));
    const activeOffers = Array.from(modules).filter(
      (offer) => offer.active && offer.giftVariantIds.length === 2 && Array.from(offer.eligibleVariantIds).some((id) => requestedIds.has(id))
    );
    if (!activeOffers.length) return null;

    if (lastSubmittedForm) {
      const contextual = activeOffers.find((offer) => {
        const scope = offer.context === 'bundle' ? offer.element.closest('bundle-deals') : offer.element.closest('[id^="MainProduct-"]');
        return scope?.contains(lastSubmittedForm);
      });
      if (contextual) return contextual;
    }
    return activeOffers[0];
  }

  function requestedSections(parsed) {
    if (Array.isArray(parsed.sections)) return parsed.sections;
    if (typeof parsed.sections === 'string' && parsed.sections.trim()) {
      return parsed.sections.split(',').map((value) => value.trim()).filter(Boolean);
    }
    const renderer = document.querySelector('cart-drawer, cart-notification');
    if (typeof renderer?.getSectionsToRender === 'function') {
      return renderer.getSectionsToRender().map((section) => section.id).filter(Boolean);
    }
    return [];
  }

  function showError(offer, message) {
    const target = offer?.element.querySelector('.tobfun-free-gifts__error');
    if (!target) return;
    target.textContent = message;
    target.hidden = false;
  }

  function mergeResponse(originalResponse, originalPayload, giftPayload) {
    if (giftPayload?.sections) originalPayload.sections = giftPayload.sections;
    const headers = new Headers(originalResponse.headers);
    headers.set('content-type', 'application/json; charset=utf-8');
    return new Response(JSON.stringify(originalPayload), {
      status: originalResponse.status,
      statusText: originalResponse.statusText,
      headers
    });
  }

  function patchFetch() {
    if (fetchPatched) return;
    fetchPatched = true;

    window.fetch = async function tobfunGiftFetch(input, init) {
      let parsed = null;
      try {
        parsed = await parseRequest(input, init);
      } catch (error) {
        parsed = null;
      }

      const offer = parsed?.items?.length ? activeOfferFor(parsed.items) : null;
      const qualifyingItems = offer
        ? parsed.items.filter((item) => offer.eligibleVariantIds.has(String(item.id)))
        : [];

      const originalResponse = await originalFetch(input, init);
      if (!offer || !qualifyingItems.length || !originalResponse.ok) return originalResponse;

      let originalPayload;
      try {
        originalPayload = await originalResponse.clone().json();
      } catch (error) {
        originalPayload = null;
      }

      const existingIds = new Set(parsed.items.map((item) => String(item.id)));
      const quantity = qualifyingItems.reduce((total, item) => total + asPositiveInteger(item.quantity), 0);
      const giftItems = offer.giftVariantIds
        .filter((id) => !existingIds.has(String(id)))
        .map((id) => ({
          id,
          quantity,
          properties: {
            _tobfun_free_gift: 'true',
            _gift_for_variant: qualifyingItems[0].id
          }
        }));

      if (!giftItems.length) return originalResponse;

      const payload = {
        items: giftItems,
        sections: requestedSections(parsed),
        sections_url: parsed.sectionsUrl || window.location.pathname
      };

      try {
        const giftResponse = await originalFetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!giftResponse.ok) throw new Error('Gift products could not be added.');
        const giftPayload = await giftResponse.json();
        document.dispatchEvent(new CustomEvent('tobfun:free-gifts-added', { detail: giftPayload }));
        return originalPayload ? mergeResponse(originalResponse, originalPayload, giftPayload) : originalResponse;
      } catch (error) {
        showError(offer, 'The main item was added, but the free gifts could not be added. Please contact us.');
        return originalResponse;
      }
    };
  }

  document.addEventListener('submit', (event) => {
    lastSubmittedForm = event.target;
    window.setTimeout(() => { lastSubmittedForm = null; }, 1500);
  }, true);

  document.addEventListener('change', () => window.requestAnimationFrame(refreshAll), true);
  document.addEventListener('input', () => window.requestAnimationFrame(refreshAll), true);
  document.addEventListener('click', () => window.setTimeout(refreshAll, 0), true);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  const observer = new MutationObserver(() => initialize(document));
  observer.observe(document.documentElement, { childList: true, subtree: true });

  initialize(document);
  patchFetch();
  window.setInterval(refreshAll, 600);
})();
